import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  chatConversationKey,
  type ChatAttachment,
  type ChatConversationResponse,
  type ChatMessageMutationResponse,
  type ChatOverviewResponse,
  type ChatTarget,
  type MarkChatReadResponse,
  type SendChatMessageResponse,
  type ToggleChatReactionResponse,
} from "@xop/dashboard-shared";

import { api, apiUpload } from "../api.js";
import { chatKeys, targetSegment } from "./keys.js";

/** The sidebar: the "All Staff" room + the full staff roster, each with this
 * viewer's unread count. Refetches on window focus so badges stay honest.
 * `enabled` lets the nav badge skip the fetch for a user who can't see Chat. */
export function useChatOverview(enabled = true) {
  return useQuery({
    queryKey: chatKeys.overview,
    enabled,
    queryFn: () => api<ChatOverviewResponse>("/api/chat/overview"),
    refetchOnWindowFocus: true,
  });
}

/** A conversation's message history (room or DM). Disabled until a target is
 * selected. Live messages are merged into this cache by the realtime provider
 * + the send mutation, so the thread updates without polling. */
export function useConversation(target: ChatTarget | null) {
  return useQuery({
    queryKey: chatKeys.conversation(target ? chatConversationKey(target) : "none"),
    enabled: target !== null,
    queryFn: () =>
      api<ChatConversationResponse>(`/api/chat/conversations/${targetSegment(target as ChatTarget)}`),
  });
}

/** Send a DM or post to the room; optimistically merge the created message
 * into the open thread and refresh the sidebar. */
export function useSendChatMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      target: ChatTarget;
      body: string;
      attachment_id?: string;
      mention_ids?: number[];
      reply_to?: number;
    }) =>
      api<SendChatMessageResponse>("/api/chat/messages", {
        method: "POST",
        body: {
          to: vars.target.kind === "room" ? vars.target.room : vars.target.user_id,
          body: vars.body,
          ...(vars.attachment_id ? { attachment_id: vars.attachment_id } : {}),
          ...(vars.mention_ids && vars.mention_ids.length > 0
            ? { mention_ids: vars.mention_ids }
            : {}),
          ...(vars.reply_to ? { reply_to: vars.reply_to } : {}),
        },
      }),
    onSuccess: (res, vars) => {
      const key = chatConversationKey(vars.target);
      qc.setQueryData<ChatConversationResponse>(chatKeys.conversation(key), (prev) => {
        if (!prev) return { messages: [res.message], has_more: false };
        if (prev.messages.some((m) => m.id === res.message.id)) return prev;
        return { ...prev, messages: [...prev.messages, res.message] };
      });
      void qc.invalidateQueries({ queryKey: chatKeys.overview });
    },
  });
}

/** Mark a conversation read up to its newest message; refresh the sidebar so
 * the unread badge clears. */
export function useMarkChatRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target: ChatTarget) =>
      api<MarkChatReadResponse>(`/api/chat/conversations/${targetSegment(target)}/read`, {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chatKeys.overview });
    },
  });
}

/** Toggle the caller's emoji reaction on a message (add/remove). The server
 * returns the message's full new reaction set; merge it into the open thread.
 * Teammates see it live via the `chat_reaction` realtime frame. */
export function useToggleReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { target: ChatTarget; messageId: number; emoji: string }) =>
      api<ToggleChatReactionResponse>(`/api/chat/messages/${vars.messageId}/reactions`, {
        method: "POST",
        body: { emoji: vars.emoji },
      }),
    onSuccess: (res, vars) => {
      const key = chatConversationKey(vars.target);
      qc.setQueryData<ChatConversationResponse>(chatKeys.conversation(key), (prev) =>
        !prev
          ? prev
          : {
              ...prev,
              messages: prev.messages.map((m) =>
                m.id === res.message_id ? { ...m, reactions: res.reactions } : m,
              ),
            },
      );
    },
  });
}

/** Edit a message body; merge the updated message into the open thread. The
 * sender's other tabs + the recipient update live via `chat_message_update`. */
export function useEditMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { target: ChatTarget; messageId: number; body: string }) =>
      api<ChatMessageMutationResponse>(`/api/chat/messages/${vars.messageId}`, {
        method: "PATCH",
        body: { body: vars.body },
      }),
    onSuccess: (res, vars) => replaceMessageInThread(qc, vars.target, res.message),
  });
}

/** Soft-delete a message; the server returns the blanked tombstone, which we
 * merge into the open thread (others see it live via `chat_message_update`). */
export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { target: ChatTarget; messageId: number }) =>
      api<ChatMessageMutationResponse>(`/api/chat/messages/${vars.messageId}`, {
        method: "DELETE",
      }),
    onSuccess: (res, vars) => {
      replaceMessageInThread(qc, vars.target, res.message);
      void qc.invalidateQueries({ queryKey: chatKeys.overview });
    },
  });
}

/** Replace a message by id in a cached conversation (edit/delete result). */
function replaceMessageInThread(
  qc: ReturnType<typeof useQueryClient>,
  target: ChatTarget,
  message: ChatMessageMutationResponse["message"],
): void {
  const key = chatConversationKey(target);
  qc.setQueryData<ChatConversationResponse>(chatKeys.conversation(key), (prev) =>
    !prev
      ? prev
      : { ...prev, messages: prev.messages.map((m) => (m.id === message.id ? message : m)) },
  );
}

/** Upload a file → returns the attachment to reference on the next send. */
export function useUploadAttachment() {
  return useMutation({
    mutationFn: (file: File) => apiUpload<ChatAttachment>("/api/chat/attachments", file),
  });
}

/** Upload a recorded voice message — same endpoint as a file, but tagged with
 * `x-voice-note` so the server marks it (and the thread renders the WhatsApp
 * waveform bubble instead of a plain audio chip). */
export function useUploadVoiceNote() {
  return useMutation({
    mutationFn: (file: File) =>
      apiUpload<ChatAttachment>("/api/chat/attachments", file, { "x-voice-note": "1" }),
  });
}
